import { useId, useState, type FormEvent } from "react";
import { UserPlus } from "lucide-react";
import { Button, Notice, Panel, TextField } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

type FormState = "closed" | "idle" | "submitting" | "success" | "error";

export function InviteColleague({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const { city } = useCity();
  const headingId = useId();
  const [state, setState] = useState<FormState>(defaultOpen ? "idle" : "closed");
  const [inviterName, setInviterName] = useState("");
  const [inviterEmail, setInviterEmail] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [practiceName, setPracticeName] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    try {
      await api.post<{ ok: true }>(`/v1/public/${city.slug}/invites`, {
        inviterEmail: inviterEmail.trim(),
        inviterName: inviterName.trim(),
        inviteeEmail: inviteeEmail.trim(),
        practiceName: practiceName.trim(),
      }, { skipAuth: true });
      track("invite_sent");
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <Panel tone="tint" aria-labelledby={headingId}>
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">Grow the local network</p>
        <h2 id={headingId} className="ds-title">Invite a colleague</h2>
        <p className="ds-small">Send one introduction email to PulseBoard. We won't follow it up.</p>
      </div>

      {state === "success" ? (
        <div className="ds-stack" style={{ gap: 12 }}>
          <Notice tone="success">Sent. They'll get one email from us and no follow-ups.</Notice>
          <Button
            variant="secondary"
            onClick={() => {
              setInviteeEmail("");
              setPracticeName("");
              setState("idle");
            }}
          >
            Invite another colleague
          </Button>
        </div>
      ) : state === "closed" ? (
        <div>
          <Button onClick={() => setState("idle")}>
            <UserPlus aria-hidden="true" />
            Send invitation
          </Button>
        </div>
      ) : (
        <form className="ds-stack" style={{ gap: 14 }} onSubmit={submit}>
          <TextField label="Your name" value={inviterName} onChange={(event) => setInviterName(event.target.value)} autoComplete="name" maxLength={120} placeholder="Optional" />
          <TextField label="Your email" required type="email" value={inviterEmail} onChange={(event) => setInviterEmail(event.target.value)} autoComplete="email" placeholder="you@practice.com.au" />
          <TextField label="Colleague's email" required type="email" value={inviteeEmail} onChange={(event) => setInviteeEmail(event.target.value)} placeholder="colleague@practice.com.au" />
          <TextField label="Practice name" value={practiceName} onChange={(event) => setPracticeName(event.target.value)} autoComplete="organization" maxLength={160} placeholder="Optional" />
          {state === "error" ? <Notice tone="error">We could not send the invitation. Check both email addresses and try again.</Notice> : null}
          <div className="ds-cluster">
            <Button type="submit" disabled={state === "submitting" || !inviterEmail.trim() || !inviteeEmail.trim()}>
              {state === "submitting" ? "Sending…" : "Send one invitation"}
            </Button>
            <Button variant="ghost" onClick={() => setState("closed")}>Cancel</Button>
          </div>
        </form>
      )}
    </Panel>
  );
}
